import type { NostrEvent } from '@nostrify/nostrify';
import { decodeGeohash, isValidGeohash } from './geohash';
import type { Merchant } from './merchants';

/**
 * BCHNostr business directory integration.
 *
 * BCHNostr (bchnostr.com/merchants) publishes business listings as an
 * addressable kind 30499 event with the following shape:
 *
 *   kind: 30499
 *   tags:
 *     ["d", "bchnostr/business"]
 *     ["t", "bch-business"]                     // marker
 *     ["t", "bch-pro-<proType>"]                // professional type
 *     ["t", "bch-biz-cat-<category>"]           // business category
 *     ["name", "<business name>"]
 *     ["bch", "<bch address>"]                  // optional
 *     ["g", "<geohash>"]                        // optional
 *     ["location", "<free text location>"]      // optional
 *     ["t", "bch-verified-business"]            // optional verified marker
 *     ["verified", "bchnostr", "<unix expiry>"] // optional
 *     ["alt", "..."]
 *   content: JSON { tagline?, picture?, onlineOnly?, category?, proType?, ... }
 *
 * These are mapped into the app's existing `Merchant` shape so they flow
 * through the same map / list / filter UI as BCHPAY.Cash listings.
 */

/** Kind used by BCHNostr business listings. */
export const BUSINESS_KIND = 30499;
/** The `d` tag for BCHNostr business listings. */
export const BUSINESS_D_TAG = 'bchnostr/business';
/** The `t` marker tag identifying a business listing. */
export const BUSINESS_TAG = 'bch-business';
/** Prefix for BCHNostr business category `t` tags. */
export const BUSINESS_CAT_PREFIX = 'bch-biz-cat-';
/** Prefix for BCHNostr professional-type `t` tags. */
export const BUSINESS_TYPE_PREFIX = 'bch-pro-';

/**
 * Map BCHNostr category IDs to this app's category IDs (see CATEGORIES in
 * merchants.ts). Categories not listed here fall through unchanged.
 */
const BCHNOSTR_CATEGORY_MAP: Record<string, string> = {
  food: 'restaurant',
  retail: 'retail',
  services: 'services',
  online: 'other',
  entertainment: 'entertainment',
  technology: 'services',
  other: 'other',
};

/** Shape of the JSON content field on a kind 30499 event. */
interface BusinessContent {
  tagline?: string;
  about?: string;
  picture?: string;
  onlineOnly?: boolean;
  category?: string;
  proType?: string;
  website?: string;
}

/** Get a single tag value from an event. */
function getTag(event: NostrEvent, name: string): string {
  return event.tags.find(([n]) => n === name)?.[1] ?? '';
}

/** Whether an event is an active (non-retired) BCHNostr business listing. */
export function isActiveBusiness(event: NostrEvent): boolean {
  if (event.kind !== BUSINESS_KIND) return false;
  const hasMarker = event.tags.some(([n, v]) => n === 't' && v === BUSINESS_TAG);
  // Retired listings have empty content.
  return hasMarker && event.content.trim() !== '';
}

/**
 * Parse a BCHNostr kind 30499 business event into the app's `Merchant` shape.
 * Returns null for retired or malformed events.
 */
export function parseBusinessEvent(event: NostrEvent): Merchant | null {
  if (!isActiveBusiness(event)) return null;

  let content: BusinessContent = {};
  try {
    content = JSON.parse(event.content) as BusinessContent;
  } catch {
    content = {};
  }

  const name = getTag(event, 'name') || content.about || '';
  if (!name) return null;

  // Category comes from the `bch-biz-cat-*` tag, falling back to content.
  const catTag = event.tags.find(
    ([n, v]) => n === 't' && typeof v === 'string' && v.startsWith(BUSINESS_CAT_PREFIX),
  );
  const rawCategory = catTag
    ? catTag[1].slice(BUSINESS_CAT_PREFIX.length)
    : content.category ?? '';
  const mappedCategory = rawCategory
    ? BCHNOSTR_CATEGORY_MAP[rawCategory] ?? rawCategory
    : '';

  const geohash = getTag(event, 'g');
  const locationText = getTag(event, 'location');

  let location: { lat: number; lng: number } | null = null;
  if (geohash && isValidGeohash(geohash)) {
    location = decodeGeohash(geohash);
  }

  // Summary: prefer explicit tagline/about, then the free-text location.
  const summary = content.tagline || content.about || locationText || '';

  const categories = mappedCategory ? [mappedCategory] : [];

  // Listings created directly through BCH Local carry a source=bch-local tag.
  const sourceTag = getTag(event, 'source');
  const source: Merchant['source'] = sourceTag === 'bch-local' ? 'bch-local' : 'bchnostr';

  // Direct listings may carry a website in their content JSON.
  const website = typeof content.website === 'string' ? content.website : '';

  // Verification is determined authoritatively by BCHNostr grant events
  // (see useBchnostrVerified / businessVerify.ts), not by a tag on the listing.
  return {
    event,
    pubkey: event.pubkey,
    name,
    summary,
    categories,
    website,
    geohash,
    location,
    lastActive: null,
    verified: false,
    sponsored: false,
    source,
  };
}
